import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Quote, Eye, Target } from "lucide-react";

import { useI18n } from "../i18n";
import { api, fmtETB } from "../lib/api";
import { PackageCards } from "../components/PackageCards";
import { ContactSection, Cinematic } from "../components/Layout";
import { TutorialSection } from "../components/Tutorials";
import { useSettings } from "../lib/providers";

const Hero = () => {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const { settings } = useSettings();
  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, [settings?.stats]);
  return (
    <section className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 pb-20 pt-16 lg:grid-cols-[1.1fr_1fr] lg:px-8 lg:pt-24">
      <div className="fade-up">
        <p className="eyebrow">{t("hero_eyebrow")}</p>
        <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
          {t("hero_title")}
        </h1>
        <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">{t("hero_sub")}</p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/register" className="btn-gold" data-testid="hero-join-btn">{t("hero_cta_join")} <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/#packages" className="btn-ghost-gold" data-testid="hero-packages-btn">{t("hero_cta_learn")}</Link>
        </div>
        <div className="mt-14 flex gap-12 border-t pt-8">
          <div><p className="font-serif text-4xl font-semibold gold-text" data-testid="stat-members">{stats ? stats.approved_members : "—"}</p><p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{t("stat_members")}</p></div>
          <div><p className="font-serif text-4xl font-semibold gold-text" data-testid="stat-support">{stats ? fmtETB(stats.total_support) : "—"}</p><p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{t("stat_support")}</p></div>
        </div>
      </div>
      <div className="relative fade-up" style={{ animationDelay: "200ms" }}>
        <div className="absolute -inset-4 rounded-[2rem] opacity-40 blur-3xl" style={{ background: "var(--gold-soft)" }} />
        <div className="relative overflow-hidden rounded-[2rem] border" style={{ borderColor: "var(--gold)" }}>
          <img src="/images/hero.jpg" alt="Hands joined together" className="h-[460px] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/20 bg-black/40 p-5 text-white backdrop-blur-xl">
            <p className="font-serif text-2xl">Helping Hands</p>
            <p className="text-xs uppercase tracking-[0.25em] text-white/70">{t("brand_tagline")}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const VisionMission = () => {
  const { t } = useI18n();
  const items = [[Eye, "vision"], [Target, "mission"]];
  return (
    <section id="about" className="mx-auto max-w-7xl px-5 py-20 lg:px-8" data-testid="vision-mission-section">
      <p className="eyebrow">{t("vm_eyebrow")}</p>
      <h2 className="mt-3 font-serif text-4xl font-semibold sm:text-5xl">{t("vm_title")}</h2>
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {items.map(([Icon, k], i) => (
          <div key={k} className="lux-card grain relative overflow-hidden p-8 md:p-10 fade-up" style={{ animationDelay: `${i * 120}ms` }} data-testid={`${k}-card`}>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: "var(--gold-soft)", border: "1px solid var(--gold)" }}><Icon className="h-5 w-5" style={{ color: "var(--gold)" }} /></span>
            <h3 className="mt-6 font-serif text-3xl font-semibold gold-text">{t(`${k}_label`)}</h3>
            <p className="mt-4 text-base leading-relaxed md:text-lg" data-testid={`${k}-text`}>{t(`${k}_text`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const TESTIMONIALS = [
  { key: "tm_1", name: "Selam T.", img: "/images/a2.jpg" },
  { key: "tm_2", name: "Abdi K.", img: "/images/a1.jpg" },
  { key: "tm_3", name: "Hiwot G.", img: "/images/a3.jpg" },
];

const Testimonials = () => {
  const { t } = useI18n();
  return (
    <section id="testimonials" className="testimonials-band py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <p className="eyebrow">{t("tm_eyebrow")}</p>
        <h2 className="mt-3 max-w-2xl font-serif text-4xl font-semibold sm:text-5xl">{t("tm_title")}</h2>
        <p className="mt-3 text-sm text-muted-foreground">{t("tm_sub")}</p>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((x, i) => (
            <figure key={x.key} className={`lux-card flex flex-col p-8 ${i === 1 ? "md:translate-y-8" : ""}`} data-testid={`testimonial-${i}`}>
              <Quote className="h-8 w-8" style={{ color: "var(--gold)" }} />
              <blockquote className="mt-5 flex-1 font-serif text-xl leading-relaxed">{t(x.key)}</blockquote>
              <figcaption className="mt-8 flex items-center gap-3">
                <img src={x.img} alt={x.name} className="h-11 w-11 rounded-full object-cover" />
                <div><p className="font-semibold">{x.name}</p><p className="text-xs text-muted-foreground">{t("tm_role")}</p></div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useI18n();
  const { hash } = useLocation();
  const { settings } = useSettings();
  useEffect(() => {
    if (hash) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 80);
    else window.scrollTo({ top: 0 });
  }, [hash]);
  return (
    <Cinematic animate>
      <Hero />
      <VisionMission />
      <section id="packages" className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <p className="eyebrow">{t("pk_eyebrow")}</p>
        <h2 className="mt-3 font-serif text-4xl font-semibold sm:text-5xl">{t("pk_title")}</h2>
        <p className="mt-4 max-w-2xl text-muted-foreground">{t("pk_sub")}</p>
        <div className="mt-14"><PackageCards /></div>
        <div className="mt-10"><Link to="/register" className="btn-gold" data-testid="packages-join-btn">{t("hero_cta_join")} <ArrowRight className="h-4 w-4" /></Link></div>
      </section>
      <Testimonials />
      {settings?.tutorials_public && <TutorialSection />}
      <ContactSection />
    </Cinematic>
  );
}
